import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Sparkles, Check, MapPin, Star, HeartPulse } from "lucide-react";
import { http } from "@/lib/api";
import useConfig from "@/lib/useConfig";
import useSeo from "@/lib/useSeo";
import Testimonials from "@/components/Testimonials";
import { useCountry } from "@/lib/country";

const HERO_IMG = "/hero-medipats.jpg";

const STEPS = [
  { n: "01", t: "Tell Us What You Need", d: "Share treatment details, timeline and medical reports securely — takes under 5 minutes." },
  { n: "02", t: "Get Your Personalised Treatment Options", d: "Receive doctor evaluations, indicative costs and hospital recommendations within 24–48 hours." },
  { n: "03", t: "Connect With Doctors & Hospitals", d: "Video consultations, written second opinions and treatment planning with vetted specialists." },
  { n: "04", t: "Plan Your Medical Journey", d: "Medical visa invitation letters, flights, accommodation, airport pickup — end-to-end." },
  { n: "05", t: "Treatment in India", d: "Dedicated on-ground care coordinator, translation, and daily support during your stay." },
  { n: "06", t: "Follow-Up & Continued Care", d: "Post-treatment tele-consultations, medical records and follow-up back home." },
];

function Stat({ v, label, to }) {
  return (
    <Link to={to} className="mp-card p-6 group" data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="font-serif text-4xl md:text-5xl text-[#F3E9EC]">{v}<span className="text-[#B47A9A]">+</span></div>
      <div className="text-[11px] uppercase tracking-widest text-[#B47A9A] font-mono mt-2">{label}</div>
      <div className="mt-4 text-sm text-[#F3E9EC]/55 group-hover:text-[#F3E9EC]/80 flex items-center gap-2">
        Explore <ArrowRight className="w-3.5 h-3.5"/>
      </div>
    </Link>
  );
}

export default function Home() {
  const [stats, setStats] = useState({});
  const [defaultTreatments, setDefaultTreatments] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [countries, setCountries] = useState([]);
  const { waLink } = useConfig();
  const { country, detail } = useCountry();
  const site = typeof window !== "undefined" ? window.location.origin : "";

  useSeo({
    title: country ? `MediPats — Medical Treatment in India for Patients from ${country.name}` : "MediPats — International Medical Concierge for Treatment in India",
    description: "MediPats helps international patients discover suitable treatment, doctors and hospitals in India while coordinating their medical journey from consultation to treatment and follow-up.",
    canonical: `${site}/`,
    image: "/hero-medipats.jpg",
    jsonLd: {
      "@context": "https://schema.org", "@type": "Organization", "name": "MediPats", "url": site,
      "description": "International healthcare concierge for patients travelling to India for medical treatment.",
    }
  });

  useEffect(() => {
    http.get("/stats").then(r => setStats(r.data));
    http.get("/treatments").then(r => setDefaultTreatments(r.data));
    http.get("/hospitals").then(r => setHospitals(r.data.slice(0, 3)));
    http.get("/countries").then(r => setCountries(r.data));
  }, []);

  // If country is selected, prioritise its featured treatments (from country detail)
  const featuredTreatments = country && detail?.treatments?.length
    ? detail.treatments.slice(0, 6)
    : defaultTreatments.slice(0, 6);

  return (
    <div>
      {/* HERO */}
      <section className="hero-glow relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 md:pt-24 md:pb-32 grid lg:grid-cols-2 gap-14 items-center">
          <div className="fade-up">
            <div className="overline mb-6 flex items-center gap-3">
              {country && <span className="text-lg">{country.flag}</span>}
              <span>{country ? `For patients travelling from ${country.name}` : "International Medical Concierge · India"}</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#F3E9EC] tracking-tight" data-testid="hero-headline">
              Planning Medical Treatment in India?<br/>
              <span className="text-[#B47A9A]">We're With You</span> From the First Step to the Last.
            </h1>
            <p className="mt-6 text-lg text-[#F3E9EC]/75 leading-relaxed max-w-xl">
              {country
                ? `MediPats coordinates medical journeys for patients travelling from ${country.name} — from doctor selection and quotes in ${country.currency} to visa, flights and airport pickup.`
                : "MediPats helps international patients discover suitable treatment, doctors and hospitals in India while coordinating their medical journey from consultation to treatment and follow-up."}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/enquiry" className="btn-primary px-7 py-4 font-medium flex items-center justify-center gap-2" data-testid="primary-cost-cta">
                Get Your Personalised Cost of Treatment <ArrowRight className="w-4 h-4"/>
              </Link>
              <a href={waLink()} target="_blank" rel="noreferrer" className="btn-whatsapp px-7 py-4 font-medium flex items-center justify-center gap-2" data-testid="secondary-whatsapp-cta">
                <MessageCircle className="w-4 h-4"/> Connect with a Care Coordinator on WhatsApp
              </a>
            </div>
            {country && (
              <div className="mt-4">
                <Link to={`/countries/${country.slug}`} className="text-sm text-[#B47A9A] hover:text-white flex items-center gap-2" data-testid="hero-country-link">
                  Full guide for patients from {country.name} <ArrowRight className="w-3.5 h-3.5"/>
                </Link>
              </div>
            )}
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-[#F3E9EC]/60">
              {["JCI & NABH accredited hospitals", "24–48 hr response", "No hidden fees"].map(x => (
                <div key={x} className="flex items-center gap-2"><Check className="w-4 h-4 text-[#B47A9A]"/>{x}</div>
              ))}
            </div>
          </div>
          <div className="relative fade-up" style={{animationDelay:".15s"}}>
            <div className="relative rounded-[28px] overflow-hidden border border-[#5E3A5C]/40" style={{aspectRatio: "4/5"}}>
              <img src={HERO_IMG} alt="MediPats care coordinator with international patient" className="w-full h-full object-cover" style={{objectPosition: "65% center"}}/>
              <div className="absolute inset-0" style={{background: "linear-gradient(180deg, transparent 40%, rgba(0,3,14,0.85) 100%)"}}/>
              <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-5">
                <div className="flex items-center gap-2 text-[#B47A9A] text-xs font-mono uppercase tracking-widest"><Sparkles className="w-3.5 h-3.5"/> Care Coordinator</div>
                <p className="mt-2 text-[#F3E9EC] font-serif text-xl">"From first enquiry to follow-up, one dedicated coordinator by your side."</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 -mt-12 relative z-10 pb-24">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat v={stats.countries || 0} label="Countries Served" to="/countries"/>
          <Stat v={stats.hospitals || 0} label="Hospitals" to="/hospitals"/>
          <Stat v={stats.doctors || 0} label="Doctors" to="/doctors"/>
          <Stat v={stats.treatments || 0} label="Treatments" to="/treatments"/>
          <Stat v={Math.max(stats.patients_assisted || 0, 50)} label="Patients Assisted" to="/enquiry"/>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="plum-section py-24 relative grain">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="overline mb-4">How MediPats Works</div>
            <h2 className="font-serif text-3xl md:text-5xl text-[#F3E9EC]">A single coordinated journey — from enquiry to recovery.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.n} className="mp-card p-7" data-testid={`journey-step-${i+1}`}>
                <div className="flex items-baseline gap-4 mb-3">
                  <span className="font-serif text-4xl text-[#B47A9A]">{s.n}</span>
                  <span className="h-px flex-1 bg-[#5E3A5C]/50"/>
                </div>
                <h3 className="font-serif text-2xl text-[#F3E9EC] mb-3">{s.t}</h3>
                <p className="text-[#F3E9EC]/65 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED TREATMENTS */}
      <section className="cream-section py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="overline mb-3" style={{color:"#5E3A5C"}}>{country ? `Popular for patients from ${country.name}` : "Featured Treatments"}</div>
              <h2 className="font-serif text-3xl md:text-5xl text-[#1A1220]">{country ? `Treatments most requested by ${country.name.split(" ")[0]} patients` : "Explore world-class care across specialties"}</h2>
            </div>
            <Link to="/treatments" className="text-[#5E3A5C] hover:text-[#2C1B2F] flex items-center gap-2 font-medium">See all treatments <ArrowRight className="w-4 h-4"/></Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredTreatments.map(t => (
              <Link to={`/treatments/${t.slug}`} key={t.id} className="mp-card p-7 block" data-testid={`treatment-${t.slug}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#F3E9EC]" style={{border: "1px solid rgba(94,58,92,0.2)"}}>
                    <HeartPulse className="w-5 h-5 text-[#5E3A5C]"/>
                  </div>
                  <span className="text-xs font-mono uppercase tracking-widest text-[#5E3A5C]">{t.category}</span>
                </div>
                <h3 className="font-serif text-2xl text-[#1A1220]">{t.name}</h3>
                <p className="mt-3 text-sm text-[#2C1B2F]/70 line-clamp-3">{t.overview}</p>
                <div className="mt-6 text-[#5E3A5C] text-sm font-medium flex items-center gap-2">View treatment <ArrowRight className="w-3.5 h-3.5"/></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOSPITALS */}
      <section className="py-24 bg-[#00030E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <h2 className="font-serif text-3xl md:text-5xl text-[#F3E9EC]">Multi-specialty hospitals with international patient programmes</h2>
            </div>
            <Link to="/hospitals" className="text-[#B47A9A] hover:text-white flex items-center gap-2 font-medium">See all hospitals <ArrowRight className="w-4 h-4"/></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {hospitals.map(h => (
              <Link to={`/hospitals/${h.slug}`} key={h.id} className="mp-card overflow-hidden block" data-testid={`hospital-${h.slug}`}>
                <div className="h-52 relative">
                  <img src={h.hero_image} alt={h.name} className="w-full h-full object-cover"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#00030E] via-transparent"/>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-xs text-[#B47A9A] font-mono uppercase tracking-widest"><MapPin className="w-3.5 h-3.5"/>{h.city}</div>
                  <h3 className="font-serif text-2xl text-[#F3E9EC] mt-2">{h.name}</h3>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {(h.specialties||[]).slice(0,3).map(s => (
                      <span key={s} className="text-[11px] px-2.5 py-1 rounded-full border border-[#5E3A5C]/60 text-[#F3E9EC]/75">{s}</span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* COUNTRIES */}
      <section className="plum-section py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="overline mb-3">Country-Specific Guidance</div>
          <h2 className="font-serif text-3xl md:text-5xl text-[#F3E9EC] mb-12">Tailored medical travel from your country</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {countries.map(c => (
              <Link to={`/countries/${c.slug}`} key={c.id} className="mp-card p-8 flex items-center justify-between" data-testid={`country-${c.slug}`}>
                <div>
                  <div className="text-4xl mb-3">{c.flag}</div>
                  <h3 className="font-serif text-2xl text-[#F3E9EC]">{c.name}</h3>
                  <p className="text-sm text-[#F3E9EC]/60 mt-2">{c.flight_hours}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#B47A9A]"/>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials limit={6} country={country?.slug}/>

      {/* CTA */}
      <section className="cream-section py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Star className="w-8 h-8 text-[#5E3A5C] mx-auto"/>
          <h2 className="font-serif text-3xl md:text-5xl mt-4 text-[#1A1220]">Start your medical journey to India — confidently.</h2>
          <p className="mt-4 text-[#2C1B2F]/70">Submit your enquiry today and receive a personalised treatment plan within 24–48 hours.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/enquiry" className="btn-primary px-8 py-4 font-medium" data-testid="footer-enquiry-cta">Get Your Personalised Cost</Link>
            <a href={waLink()} target="_blank" rel="noreferrer" className="btn-whatsapp px-8 py-4 font-medium">Chat on WhatsApp</a>
          </div>
        </div>
      </section>
    </div>
  );
}
